<script setup lang="ts">
  import { useWindowSize } from '@vueuse/core'
  import {
    calculateAutoLayoutColumnsForWidth,
    COLUMNS_MINIMUM_WIDTH,
  } from '../../utils/layout/calculateAutoLayoutColumnsForWidth'

  export interface TabItem {
    label: string
    id: string
    disableSelection?: boolean
    onClick?: () => void
  }

  interface TabProps {
    tabs: TabItem[]
    design?: 'google' | 'msn'
    setMaxWidth?: boolean
  }

  const props = withDefaults(defineProps<TabProps>(), {
    design: 'google',
    setMaxWidth: true,
  })

  const selected = defineModel<string>('selected')
  const visibleTabs = ref<TabItem[]>(props.tabs)
  const overflowTabs = ref<TabItem[]>([])

  const emit = defineEmits<{
    (event: 'tabClick', tab: TabItem): void
  }>()

  function handleItemClick(tab: TabItem) {
    if (tab.onClick) tab.onClick()
    if (!tab.disableSelection) selected.value = tab.id
    emit('tabClick', tab)
    closeMenu()
  }

  const openMenu = ref(false)
  function toggleMenu() {
    openMenu.value = !openMenu.value
  }

  function closeMenu() {
    openMenu.value = false
  }

  function updateVisibleTabs(containerWidth: number) {
    let currentWidth = 0
    const newVisibleTabs: TabItem[] = []
    const newOverflowTabs: TabItem[] = []

    // Account for the width of the overflow menu button and gaps between tabs
    const overflowButtonWidth = 40
    const gapWidth = props.design === 'google' ? 24 : 8
    props.tabs.forEach((tab) => {
      const tabElement = document.querySelector(`[data-tab-id="${tab.id}"]`)
      if (tabElement instanceof HTMLElement) {
        const tabWidth = tabElement?.offsetWidth ?? 70
        if (
          newOverflowTabs.length === 0 &&
          currentWidth + tabWidth + overflowButtonWidth + gapWidth <
            containerWidth
        ) {
          newVisibleTabs.push(tab)
          currentWidth += tabWidth + gapWidth
        } else {
          newOverflowTabs.push(tab)
        }
      }
    })

    visibleTabs.value = newVisibleTabs
    overflowTabs.value = newOverflowTabs
  }

  const { width } = useWindowSize()
  const availableSpace = computed(() => {
    const columns = calculateAutoLayoutColumnsForWidth(width.value)

    return COLUMNS_MINIMUM_WIDTH[columns]
  })

  watch(
    [() => props.tabs, () => availableSpace.value],
    () => {
      nextTick(() => {
        updateVisibleTabs(availableSpace.value)
      })
    },
    { deep: true, immediate: true }
  )
</script>

<template>
  <div
    class="hidden lg:flex w-full flex-row overflow-x-clip items-center"
    :style="[setMaxWidth ? { maxWidth: `${availableSpace}px` } : {}]"
  >
    <div
      :class="{
        'flex flex-row gap-24px': design === 'google',
        'flex flex-row gap-8px': design === 'msn',
      }"
      data-testid="tab-container"
    >
      <TabItem
        v-for="tab in props.tabs"
        :key="tab.id"
        :class="{
          'invisible absolute': !visibleTabs.includes(tab),
        }"
        :label="tab.label"
        :selected="tab.id === selected"
        :data-tab-id="tab.id"
        :design="design"
        @mousedown="handleItemClick(tab)"
        @keydown.enter="handleItemClick(tab)"
      />
      <div
        v-if="overflowTabs.length > 0"
        v-click-outside="closeMenu"
        class="relative"
        :class="{ 'flex items-center': design === 'msn' }"
      >
        <IconButton icon="dots-horizontal" class="p-4px" @click="toggleMenu" />
        <ul
          v-if="openMenu"
          :class="{
            'px-12px py-12px': design === 'msn',
            'px-24px pt-24px pb-12px': design === 'google',
          }"
          class="rounded-4px space-y-3 border-border-interaction-neutral-normal bg-background-default absolute right-0 top-full z-10 w-fit border dark:bg-background-neutral-subtle"
        >
          <li v-for="tab in overflowTabs" :key="tab.id">
            <TabItem
              :label="tab.label"
              :selected="tab.id === selected"
              :design="design"
              @mousedown="handleItemClick(tab)"
            />
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
